import { Permission, RoleForResolver, User, UserForResolver } from "db/definitions";
import { db } from "../db";
import { buildRole, bulkAddRoles, bulkGetRoles, createRoleForResolver, deleteAllRoleDocs } from "./roleQueries";
import { bulkAddPerms, deleteAllPerms } from "./permissionQueries";

/**
 * Given a user document from the cloud, saves it's analytics offline and the user itself offline
 * @author Eric Webb <ewebb@factorearth.com>
 * @param userDoc The user doc we're trying to create
 */
export async function createUser(userDoc: UserForResolver): Promise<void> {
	await db.user.put(userDoc);
}

/**
 * Given a user doc, returns it with only the doc-specific properties
 * @author Eric Webb <ewebb@factorearth.com>
 * @param user The user doc that we need to parse
 * @returns The parsed version of the user doc
 */
export function createUserDocForResolver(user: User | UserForResolver): UserForResolver {
	const userToReturn: UserForResolver = {
		id: user.id,
		roleIds: [...user.roleIds],
		username: user.username,
		email: user.email,
		group: user.group,
		hideTutorial: user.hideTutorial,
		isDeveloper: user.isDeveloper,
		lightMode: user.lightMode,
		createdAt: user.createdAt,
		updatedAt: user.updatedAt,
	};
	return userToReturn;
}

/**
 * Deletes all user docs in the indexedDb
 * @author Eric Webb <ewebb@factorearth.com>
 */
async function deleteAllUserDocs(): Promise<void> {
	await db.user.clear();
}

/**
 * Gets the first user document from the database
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The first user in the database
 */
export async function getFirstUser(): Promise<User | undefined> {
	const users = await db.user.toArray();
	const user = users[0];
	if (!user) return;
	const rolesForResolver = await bulkGetRoles(user.roleIds);
	const finalRolePromises = [];
	for (const role of rolesForResolver) {
		finalRolePromises.push(buildRole(role));
	}
	const roles = await Promise.all(finalRolePromises);
	const finalUser: User = {
		...user,
		roles,
	};
	return finalUser;
}

/**
 * Queries the count of all users, multiplies the count by the average size, and returns that value
 * @author Eric Webb <ewebb@factorearth.com>
 * @returns The filesize, in bytes, of all users
 */
export async function getTotalUserDiskSize(): Promise<number> {
	const number = await db.user.count();
	const AVERAGE_DOCUMENT_SIZE = 154.15;
	return number * AVERAGE_DOCUMENT_SIZE;
}

/**
 * Given a user document from the cloud, we clear the user table and analytic table, then save the new user offline
 * @author Eric Webb <ewebb@factorearth.com>
 * @param userDoc The user document we want to save offline
 */
export async function replaceUserInDatabase(userDoc: User): Promise<void> {
	await Promise.all([deleteAllUserDocs(), deleteAllRoleDocs(), deleteAllPerms()]);

	const parsedUserDoc = createUserDocForResolver(userDoc);
	const roles: RoleForResolver[] = [];
	const permissions: Permission[] = [];
	const permissionIds = new Set<string>();

	for (const role of userDoc.roles) {
		roles.push(createRoleForResolver(role));
		for (const perm of role.permissions) {
			if (!permissionIds.has(perm.id)) {
				permissionIds.add(perm.id);
				permissions.push(perm);
			}
		}
	}

	await Promise.all([createUser(parsedUserDoc), bulkAddRoles(roles), bulkAddPerms(permissions)]);
}
